import React, { useEffect, useMemo, useState, useRef } from "react";
import { api } from "@/lib/api";
import { money } from "@/lib/format";
import { PageHeader, Loader, VegDot, EmptyState } from "@/components/common/Bits";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Search, Clock, Plus, Pencil, Trash2 } from "lucide-react";

const SUGGESTED_CATEGORIES = [
  "Starters",
  "Main Course",
  "Breads",
  "Rice & Biryani",
  "Chinese",
  "Beverages",
  "Desserts",
  "Combos",
];

import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import ConfirmDialog from "@/components/common/ConfirmDialog";
export default function Menu() {
  const [cats, setCats] = useState([]);
  const [items, setItems] = useState(null);
  const [activeCat, setActiveCat] = useState("all");
  const [q, setQ] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [categoryBusy, setCategoryBusy] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const [form, setForm] = useState({
    name: "",
    description: "",
    price: "",
    category_id: "",
    prep_time: "",
    station: "",
    image: "",
    veg: true,
  });

  useEffect(() => {
    api
      .categories()
      .then((r) => setCats(r.data))
      .catch(() => {});
    api
      .menuItems()
      .then((r) => setItems(r.data))
      .catch(() => {});
  }, []);

  const createCategory = async (name) => {
    const cleanName = (name || "").trim();
    if (!cleanName) {
      toast.error("Category name is required");
      return null;
    }

    const existing = cats.find(
      (c) => c.name?.trim().toLowerCase() === cleanName.toLowerCase(),
    );
    if (existing) {
      setForm((f) => ({ ...f, category_id: existing.id }));
      toast.success("Category selected");
      return existing;
    }

    setCategoryBusy(true);
    try {
      const res = await api.createCategory({ name: cleanName });
      const created = res.data || res;
      if (!created?.id) throw new Error("Category was not created");

      setCats((current) => [...current, created]);
      setForm((f) => ({ ...f, category_id: created.id }));
      setNewCategoryName("");
      setCategoryDialogOpen(false);
      toast.success(`Category "${created.name}" created`);
      return created;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create category");
      return null;
    } finally {
      setCategoryBusy(false);
    }
  };

  const handleSuggestedCategory = async (name) => {
    const existing = cats.find(
      (c) => c.name?.trim().toLowerCase() === name.toLowerCase(),
    );
    if (existing) {
      setForm((f) => ({ ...f, category_id: existing.id }));
      toast.success(`${name} selected`);
      return;
    }
    await createCategory(name);
  };

  const filtered = useMemo(
    () =>
      (items || []).filter(
        (i) =>
          (activeCat === "all" || i.category_id === activeCat) &&
          (!q || i.name.toLowerCase().includes(q.toLowerCase())),
      ),
    [items, activeCat, q],
  );

  const toggle = async (item) => {
    setItems((arr) =>
      arr.map((x) =>
        x.id === item.id ? { ...x, available: !x.available } : x,
      ),
    );
    try {
      await api.toggleAvailability(item.id, !item.available);
      toast.success(
        `${item.name} ${!item.available ? "available" : "out of stock"}`,
      );
    } catch (e) {
      toast.error("Failed");
    }
  };

  const handleDelete = (item) => {
    setDeleteItem(item);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteItem) return;

    setDeleteBusy(true);

    try {
      await api.deleteMenuItem(deleteItem.id);

      setItems((arr) => arr.filter((x) => x.id !== deleteItem.id));

      toast.success("Menu item deleted");
      setDeleteDialogOpen(false);
      setDeleteItem(null);
    } catch (e) {
      toast.error(
        e.response?.data?.detail || "Failed to delete menu item",
      );
    } finally {
      setDeleteBusy(false);
    }
  };

  if (!items)
    return (
      <div className="p-6">
        <Loader label="Loading menu" />
      </div>
    );

  return (
    <>
      <div className="p-4 sm:p-6 animate-fade-up">
        <PageHeader
          title="Menu Management"
          subtitle={`${items.length} items across ${cats.length} categories`}
          actions={
            <Button
              onClick={() => {
                setEditingItem(null);
                setForm({
                  name: "",
                  description: "",
                  price: "",
                  category_id: "",
                  prep_time: "",
                  station: "",
                  image: "",
                  veg: true,
                });
                setDialogOpen(true);
              }}
              className="rounded-xl font-semibold"
            >
              <Plus size={16} className="mr-1" />
              Add Menu Item
            </Button>
          }
        />
        <div className="flex flex-col sm:flex-row gap-3 mb-5">
          <div className="relative flex-1 max-w-sm">
            <Search
              size={18}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
            />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search items…"
              className="pl-10 h-11 rounded-xl"
              data-testid="menu-search"
            />
          </div>
          <div className="flex gap-2 overflow-x-auto no-scrollbar">
            <Cat
              active={activeCat === "all"}
              onClick={() => setActiveCat("all")}
            >
              All
            </Cat>
            {cats.map((c) => (
              <Cat
                key={c.id}
                active={activeCat === c.id}
                onClick={() => setActiveCat(c.id)}
              >
                {c.name}
              </Cat>
            ))}
          </div>
        </div>

        {filtered.length === 0 ? (
          <EmptyState
            icon={Search}
            title={items.length === 0 ? "No menu items yet" : "No matching menu items"}
            subtitle={
              items.length === 0
                ? "Add your first menu item using the Add Menu Item button above."
                : "Try a different search term or category."
            }
          />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 xl:grid-cols-4 gap-4">
            {filtered.map((item) => (
              <div
              key={item.id}
              data-testid={`menu-card-${item.id}`}
              className={cn(
                "rounded-2xl bg-card border border-border overflow-hidden shadow-[0_2px_8px_rgba(28,25,23,0.04)] transition-opacity",
                !item.available && "opacity-70",
              )}
            >
              <div className="relative aspect-[16/10] bg-stone-100">
                <img
                  src={item.image}
                  alt={item.name}
                  loading="lazy"
                  className="h-full w-full object-cover"
                />
                <div className="absolute top-2 left-2">
                  <VegDot veg={item.veg} className="bg-white/90" />
                </div>
                <div className="absolute top-2 right-2 rounded-full bg-white/90 backdrop-blur px-2 py-0.5 text-xs font-bold text-primary">
                  {money(item.price)}
                </div>
              </div>
              <div className="p-3.5">
                <p className="font-semibold leading-tight">{item.name}</p>
                <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">
                  {item.description}
                </p>
                <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Clock size={12} /> {item.prep_time}m
                  </span>
                  <span className="rounded-full bg-secondary px-2 py-0.5 font-medium">
                    {item.station}
                  </span>
                </div>
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-border">
                  <span
                    className={cn(
                      "text-xs font-bold",
                      item.available ? "text-emerald-600" : "text-red-600",
                    )}
                  >
                    {item.available ? "Available" : "Out of stock"}
                  </span>

                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => {
                        setEditingItem(item);
                        setForm({
                          name: item.name || "",
                          description: item.description || "",
                          price: item.price ?? "",
                          category_id: item.category_id || "",
                          prep_time: item.prep_time ?? "",
                          station: item.station || "",
                          image: item.image || "",
                          veg: !!item.veg,
                        });
                        setDialogOpen(true);
                      }}
                      className="h-8 w-8 inline-flex items-center justify-center rounded-lg hover:bg-secondary"
                      title="Edit menu item"
                    >
                      <Pencil size={15} />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(item)}
                      className="h-8 w-8 inline-flex items-center justify-center rounded-lg text-red-600 hover:bg-red-50"
                      title="Delete menu item"
                    >
                      <Trash2 size={15} />
                    </button>
                    <Switch
                      checked={item.available}
                      onCheckedChange={() => toggle(item)}
                      data-testid={`menu-toggle-${item.id}`}
                    />
                  </div>
                </div>
              </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <CategoryDialog
        open={categoryDialogOpen}
        value={newCategoryName}
        setValue={setNewCategoryName}
        busy={categoryBusy}
        onClose={() => {
          if (!categoryBusy) {
            setCategoryDialogOpen(false);
            setNewCategoryName("");
          }
        }}
        onSave={() => createCategory(newCategoryName)}
      />

      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete Menu Item"
        message={
          deleteItem
            ? `Delete "${deleteItem.name}" from the menu?`
            : "Are you sure you want to delete this menu item?"
        }
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={confirmDelete}
        onCancel={() => {
          if (!deleteBusy) {
            setDeleteDialogOpen(false);
            setDeleteItem(null);
          }
        }}
        loading={deleteBusy}
        danger
      />

      <MenuItemDialog
        open={dialogOpen}
        item={editingItem}
        form={form}
        setForm={setForm}
        categories={cats}
        suggestedCategories={SUGGESTED_CATEGORIES}
        onCreateCategory={() => setCategoryDialogOpen(true)}
        onSuggestedCategory={handleSuggestedCategory}
        onClose={() => setDialogOpen(false)}
        onDone={() => {
          api
            .menuItems()
            .then((r) => setItems(r.data))
            .catch(() => {});
        }}
      />
    </>
  );
}
const MenuItemDialog = ({
  open,
  item,
  form,
  setForm,
  categories,
  suggestedCategories,
  onCreateCategory,
  onSuggestedCategory,
  onClose,
  onDone,
}) => {
  const isEdit = !!item;
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState({});
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadPreview, setUploadPreview] = useState("");
  const [uploadUploading, setUploadUploading] = useState(false);
  const fileInputRef = useRef(null);

  const change = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => {
      const copy = { ...e };
      delete copy[key];
      return copy;
    });
  };

  const handleFileChange = (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    const allowed = ["image/jpeg", "image/png", "image/webp"];
    if (!allowed.includes(f.type)) {
      setErrors((x) => ({ ...(x || {}), upload: "Unsupported file type" }));
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      setErrors((x) => ({ ...(x || {}), upload: "File too large (max 5 MB)" }));
      return;
    }
    setUploadFile(f);
    try {
      const url = URL.createObjectURL(f);
      setUploadPreview(url);
    } catch (e) {
      setUploadPreview("");
    }
    setErrors((x) => {
      const c = { ...(x || {}) };
      delete c.upload;
      return c;
    });
  };

  useEffect(() => {
    return () => {
      try {
        if (uploadPreview && uploadPreview.startsWith("blob:")) URL.revokeObjectURL(uploadPreview);
      } catch (e) {}
    };
  }, [uploadPreview]);

  useEffect(() => {
    // when dialog opens or item changes, set preview from existing image
    setUploadFile(null);
    setUploadPreview(form.image || "");
    setErrors({});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, item]);

  const validateField = (key, value) => {
    const v = typeof value === "string" ? value.trim() : value;
    switch (key) {
      case "name":
        if (!v) return "Name is required";
        if (v.length < 2) return "Name must be at least 2 characters";
        return null;
      case "price":
        if (v === "" || v === null || v === undefined) return "Price is required";
        if (isNaN(Number(v))) return "Price must be a number";
        if (Number(v) <= 0) return "Price must be greater than 0";
        return null;
      case "category_id":
        if (!v) return "Category is required";
        return null;
      case "prep_time":
        if (v === "" || v === null || v === undefined) return "Prep time is required";
        if (isNaN(Number(v))) return "Prep time must be a number";
        if (Number(v) <= 0) return "Prep time must be a positive number";
        return null;
      case "station":
        if (!v) return "Station is required";
        return null;
      case "image":
        if (!v) return null;
        try {
          const u = new URL(v);
          if (u.protocol !== "http:" && u.protocol !== "https:") return "Image URL must be http or https";
        } catch (e) {
          return "Invalid image URL";
        }
        return null;
      default:
        return null;
    }
  };

  const validateAll = () => {
    const toValidate = {
      name: form.name,
      price: form.price,
      category_id: form.category_id,
      prep_time: form.prep_time,
      station: form.station,
      image: form.image,
    };
    const newErrors = {};
    Object.keys(toValidate).forEach((k) => {
      const err = validateField(k, toValidate[k]);
      if (err) newErrors[k] = err;
    });
    // validate uploadFile separately
    if (uploadFile) {
      const allowed = ["image/jpeg", "image/png", "image/webp"];
      if (!allowed.includes(uploadFile.type)) newErrors.upload = "Unsupported file type";
      if (uploadFile.size > 5 * 1024 * 1024) newErrors.upload = "File too large (max 5 MB)";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const save = async () => {
    // trim fields
    setForm((f) => ({ ...f, name: (f.name || "").trim(), description: (f.description || "").trim(), station: (f.station || "").trim(), image: (f.image || "").trim() }));

    if (!validateAll()) return;

    setBusy(true);

    let uploadedUrl = null;

    try {
      if (uploadFile) {
        setUploadUploading(true);
        try {
          const res = await api.uploadImage(uploadFile);
          uploadedUrl = res.data?.url || res?.url;
        } catch (e) {
          toast.error(e.response?.data?.detail || "Failed to upload image");
          setUploadUploading(false);
          setBusy(false);
          return;
        } finally {
          setUploadUploading(false);
        }
      }

      const body = {
        name: (form.name || "").trim(),
        description: (form.description || "").trim(),
        price: Number(form.price) || 0,
        category_id: form.category_id,
        prep_time: Number(form.prep_time) || 0,
        station: (form.station || "").trim(),
        image: uploadedUrl || (form.image || "").trim(),
        veg: !!form.veg,
      };

      if (isEdit) {
        await api.updateMenuItem(item.id, body);
        toast.success("Menu item updated");
      } else {
        await api.createMenuItem(body);
        toast.success("Menu item added");
      }

      onDone();
      onClose();
    } catch (e) {
      toast.error(
        e.response?.data?.detail ||
          `Failed to ${isEdit ? "update" : "add"} item`,
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-white sm:max-w-lg max-h-[90vh] px-5 overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="font-display">
            {isEdit ? "Edit Menu Item" : "Add Menu Item"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 flex-1 min-h-0 overflow-y-auto pr-1 pb-2 px-1">
          <div>
            <Input
              value={form.name}
              onChange={(e) => change("name", e.target.value)}
              placeholder="Item name *"
            />
            {errors.name && (
              <p className="text-xs text-red-600 mt-1">{errors.name}</p>
            )}
          </div>

          <Input
            value={form.description}
            onChange={(e) => change("description", e.target.value)}
            placeholder="Description"
          />

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Input
                type="number"
                min="1"
                value={form.price}
                onChange={(e) => change("price", e.target.value)}
                placeholder="Price"
              />
              {errors.price && (
                <p className="text-xs text-red-600 mt-1">{errors.price}</p>
              )}
            </div>

            <div>
              <select
                onChange={(e) => change("category_id", e.target.value)}
                value={form.category_id}
                className="h-10 w-full rounded-xl border border-border bg-white px-3 text-sm"
                data-testid="menu-category"
              >
                <option value="">Select category...</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
              {errors.category_id && (
                <p className="text-xs text-red-600 mt-1">{errors.category_id}</p>
              )}
            </div>
          </div>

          <div className="rounded-xl border border-border bg-secondary/30 p-3">
            <div className="flex items-center justify-between gap-3 mb-2">
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Suggested categories</p>
                <p className="text-[11px] text-muted-foreground mt-0.5">Choose one or create your own</p>
              </div>
              <button
                type="button"
                onClick={onCreateCategory}
                className="shrink-0 inline-flex items-center gap-1.5 rounded-lg border border-primary/20 bg-white px-2.5 py-1.5 text-xs font-semibold text-primary hover:bg-accent"
                data-testid="create-category"
              >
                <Plus size={14} /> Create New
              </button>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {suggestedCategories.map((name) => {
                const selected = categories.some(
                  (c) => c.id === form.category_id && c.name?.toLowerCase() === name.toLowerCase(),
                );
                return (
                  <button
                    key={name}
                    type="button"
                    onClick={() => onSuggestedCategory(name)}
                    className={cn(
                      "rounded-full border px-2.5 py-1 text-xs font-medium transition-colors",
                      selected
                        ? "border-primary bg-accent text-primary"
                        : "border-border bg-white hover:bg-secondary",
                    )}
                  >
                    {name}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Input
                type="number"
                min="1"
                value={form.prep_time}
                onChange={(e) => change("prep_time", e.target.value)}
                placeholder="Prep time (minutes)"
              />
              {errors.prep_time && (
                <p className="text-xs text-red-600 mt-1">{errors.prep_time}</p>
              )}
            </div>

            <div>
              <select
                value={form.station}
                onChange={(e) => change("station", e.target.value)}
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
                data-testid="menu-station"
              >
                <option value="">Select station...</option>
                <option value="Kitchen">Kitchen</option>
                <option value="Tandoor">Tandoor</option>
                <option value="Bar">Bar</option>
                <option value="Bakery">Bakery</option>
              </select>
              {errors.station && (
                <p className="text-xs text-red-600 mt-1">{errors.station}</p>
              )}
            </div>
          </div>

          <div>
            <label className="text-sm font-medium">Image</label>
            <Input
              value={form.image}
              onChange={(e) => change("image", e.target.value)}
              placeholder="Image URL"
            />
            {errors.image && (
              <p className="text-xs text-red-600 mt-1">{errors.image}</p>
            )}

            <div className="text-center text-xs text-muted-foreground my-2">OR</div>

            <div className="flex items-center gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={handleFileChange}
                data-testid="image-upload"
              />

              <Button
                onClick={() => fileInputRef.current && fileInputRef.current.click()}
                className="rounded-xl"
              >
                Upload Image
              </Button>
              {errors.upload && (
                <p className="text-xs text-red-600 mt-1">{errors.upload}</p>
              )}
            </div>

            {(uploadPreview || form.image) && (
              <div className="mt-2 flex items-center gap-3">
                <img
                  src={uploadPreview || form.image}
                  alt="preview"
                  className="h-20 w-20 object-contain rounded border border-border bg-stone-50"
                />
                <div className="flex flex-col">
                  <Button
                    variant="ghost"
                    onClick={() => {
                      // remove both uploaded and url image
                      if (uploadPreview && uploadPreview.startsWith("blob:")) {
                        try {
                          URL.revokeObjectURL(uploadPreview);
                        } catch (e) {}
                      }
                      setUploadFile(null);
                      setUploadPreview("");
                      setForm((f) => ({ ...f, image: "" }));
                    }}
                    className="text-sm"
                  >
                    Remove
                  </Button>
                </div>
              </div>
            )}
          </div>

          <label className="flex items-center gap-3 text-sm">
            <input
              type="checkbox"
              checked={form.veg}
              onChange={(e) => change("veg", e.target.checked)}
            />
            Vegetarian
          </label>
        </div>

        <DialogFooter>
          <Button
            onClick={save}
            disabled={busy}
            className="w-full h-11 rounded-xl font-semibold"
          >
            {busy ? "Saving..." : isEdit ? "Save Changes" : "Add Menu Item"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const CategoryDialog = ({ open, value, setValue, busy, onClose, onSave }) => (
  <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
    <DialogContent className="bg-white sm:max-w-sm max-h-[90vh] overflow-y-auto" data-testid="category-dialog">
      <DialogHeader>
        <DialogTitle className="font-display">Create New Category</DialogTitle>
      </DialogHeader>
      <div className="space-y-2">
        <Input
          autoFocus
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") onSave();
          }}
          placeholder="e.g. South Indian, Pizza, Snacks"
          className="h-11 rounded-xl"
          data-testid="new-category-name"
        />
        <p className="text-xs text-muted-foreground">This category will be saved and available for future menu items.</p>
      </div>
      <DialogFooter className="gap-2 sm:gap-2">
        <Button type="button" variant="outline" onClick={onClose} className="rounded-xl" disabled={busy}>Cancel</Button>
        <Button type="button" onClick={onSave} disabled={busy} className="rounded-xl font-semibold" data-testid="save-category">
          {busy ? "Creating..." : "Create Category"}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

const Cat = ({ active, children, ...p }) => (
  <button
    {...p}
    className={cn(
      "shrink-0 rounded-full px-4 py-2 text-sm font-semibold whitespace-nowrap transition-colors",
      active
        ? "bg-primary text-white"
        : "bg-white border border-border text-foreground hover:bg-secondary",
    )}
  >
    {children}
  </button>
);